/**
 * HelpdeskClientPortal — Iter 98 Phase B.
 * Public route: /client/portal
 *
 * Modes (URL-driven):
 *   /client/portal?ws=<workspace_id>            → login / signup / magic-link
 *   /client/portal?ws=<ws>&magic=<token>        → consumes magic link, sets JWT
 *   /client/portal  (authed via co_client_token) → ticket list + create + detail
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Ticket, Send, Plus, LogOut, ArrowLeft, ShieldCheck, Loader2,
         Star, Paperclip, Upload, RotateCcw, Filter, Search } from "lucide-react";
import BotCheck from "@/components/public/BotCheck";
import { apiErrorMessage } from "@/lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const TOKEN_KEY = "acq_client_token";
const USER_KEY = "acq_client_user";
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ─────────────────────── Root ───────────────────────

export default function HelpdeskClientPortal() {
  const [sp, setSp] = useSearchParams();
  const [wsInfo, setWsInfo] = useState(null);
  const [wsInfoErr, setWsInfoErr] = useState(null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [booting, setBooting] = useState(false);

  const wsParam = sp.get("ws");
  const brandParam = sp.get("brand");
  const magic = sp.get("magic");
  // Iter 253x · Custom domain resolver — if there's no ?ws= param but the
  // host is a mapped custom domain, look it up and continue.
  const [hostResolved, setHostResolved] = useState(null);   // {workspace_id, brand_id}
  useEffect(() => {
    if (wsParam || hostResolved) return;
    const host = typeof window !== "undefined" ? window.location.host : "";
    if (!host) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/portal/resolve-by-host`,
                                       { params: { host } });
        if (r.data?.workspace_id) {
          setHostResolved({
            workspace_id: r.data.workspace_id,
            brand_id: r.data.brand_id,
          });
        }
      } catch {
        /* not a custom domain — that's fine, wsParam UI will show */
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Consume magic link on load
  useEffect(() => {
    if (!magic) return;
    (async () => {
      setBooting(true);
      try {
        const r = await axios.post(`${API}/public/client-portal/consume-magic-link`,
                                    { token: magic });
        setSession(r.data);
        setUser(r.data.user);
        toast.success("Signed in via magic link");
      } catch (e) {
        toast.error(apiErrorMessage(e, "Invalid or expired link"));
      } finally {
        sp.delete("magic");
        setSp(sp, { replace: true });
        setBooting(false);
      }
    })();
    // eslint-disable-next-line
  }, [magic]);

  // Verify existing token still valid
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/client-portal/me`, { headers: authHeaders() });
        setUser(r.data.user);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
      } catch {
        clearSession();
        setUser(null);
      }
    })();
  }, []);

  // Fetch workspace info if we have a ws param and no user
  useEffect(() => {
    const wsId = user?.workspace_id || wsParam || hostResolved?.workspace_id;
    if (!wsId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/client-portal/workspace-info`,
                                    { params: { workspace_id: wsId } });
        setWsInfo(r.data);
      } catch (e) {
        setWsInfoErr(e?.response?.data?.detail || "Workspace not found");
      }
    })();
  }, [user?.workspace_id, wsParam, hostResolved?.workspace_id]);

  const setSession = (payload) => {
    localStorage.setItem(TOKEN_KEY, payload.token);
    localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
  };
  const clearSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  };
  const logout = () => { clearSession(); setUser(null); };

  const theme = useMemo(() => {
    const b = (wsInfo?.brands || [])[0];
    return {
      primary: b?.primary_color || "#0f172a",
      logo: b?.logo_url || wsInfo?.workspace?.brand_logo_url || null,
      name: wsInfo?.workspace?.name || "Support",
      // Iter 253v · Portal tagline shown under the brand name in the header.
      tagline: b?.portal_tagline || null,
    };
  }, [wsInfo]);

  // Boot spinner during magic-link exchange
  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="hd-client-portal">
      <Toaster richColors position="top-center" />
      <PortalHeader theme={theme} user={user} onLogout={logout} />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {!user ? (
          <AuthScreen wsId={wsParam || hostResolved?.workspace_id} wsInfo={wsInfo} wsInfoErr={wsInfoErr}
                       theme={theme} onLoggedIn={(payload) => {
                         setSession(payload); setUser(payload.user);
                       }} />
        ) : (
          <AuthedShell user={user} theme={theme} onLogout={logout} />
        )}
      </div>
    </div>
  );
}

function PortalHeader({ theme, user, onLogout }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-20"
             data-testid="hd-portal-header"
             style={{ borderTop: `3px solid ${theme.primary}` }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
        {theme.logo
          ? <img src={theme.logo} alt="" className="h-8 w-auto object-contain" />
          : <div className="h-8 w-8 rounded-full grid place-items-center text-white text-sm font-bold"
                 style={{ background: theme.primary }}>{(theme.name || "S")[0]}</div>}
        <div className="min-w-0">
          <div className="font-bold text-sm leading-tight truncate">{theme.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">
            {theme.tagline || "Support portal"}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {user && (
            <>
              <span className="text-xs text-slate-500 hidden sm:inline">{user.email}</span>
              <button onClick={onLogout} data-testid="hd-portal-logout-btn"
                      className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                <LogOut className="w-3 h-3" /> Sign out
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ─────────────────────── Auth screen (signup/login/magic) ───────────────────────

function AuthScreen({ wsId, wsInfo, wsInfoErr, theme, onLoggedIn }) {
  const [tab, setTab] = useState("login");
  return (
    <div className="max-w-md mx-auto pt-8">
      {!wsId ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="hd-portal-missing-ws">
          <ShieldCheck className="w-6 h-6 mx-auto text-slate-400" />
          <h1 className="mt-3 text-lg font-bold">Workspace required</h1>
          <p className="text-sm text-slate-500 mt-2">
            To sign in, use the invitation link from your service provider.
            The URL must include a <code className="bg-slate-100 px-1 rounded">?ws=</code> parameter.
          </p>
        </div>
      ) : wsInfoErr ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center text-sm text-red-800" data-testid="hd-portal-ws-error">
          {wsInfoErr}
        </div>
      ) : !wsInfo ? (
        <div className="text-center text-sm text-slate-400 py-10">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-3 border-b border-slate-200 text-xs">
            {["login", "signup", "magic"].map((k) => (
              <button key={k} onClick={() => setTab(k)}
                      data-testid={`hd-portal-tab-${k}`}
                      className={`py-3 uppercase tracking-widest font-semibold transition-colors ${
                        tab === k ? "text-white" : "text-slate-400 hover:text-slate-700"
                      }`}
                      style={tab === k ? { background: theme.primary } : {}}>
                {k === "magic" ? "Email link" : k}
              </button>
            ))}
          </div>
          <div className="p-5">
            {tab === "login" && <LoginForm wsId={wsId} theme={theme} onLoggedIn={onLoggedIn} />}
            {tab === "signup" && <SignupForm wsId={wsId} theme={theme} onLoggedIn={onLoggedIn} />}
            {tab === "magic" && <MagicLinkForm wsId={wsId} theme={theme} />}
          </div>
        </div>
      )}
      <p className="text-[11px] text-center text-slate-400 mt-4">
        Secure client portal • Data stays inside your workspace
      </p>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <input {...rest}
             className="mt-1 block w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-500" />
    </label>
  );
}

function LoginForm({ wsId, theme, onLoggedIn }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [bot, setBot] = useState(null);
  const [loading, setL] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setL(true);
    try {
      const r = await axios.post(`${API}/public/client-portal/login`,
                                  { ...f, workspace_id: wsId, bot_check: bot });
      onLoggedIn(r.data);
      toast.success("Welcome back");
    } catch (e2) {
      toast.error(apiErrorMessage(e2, "Login failed"));
    } finally { setL(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="hd-portal-login-form">
      <Field label="Email" type="email" required value={f.email}
             onChange={(e) => setF({ ...f, email: e.target.value })}
             data-testid="hd-portal-login-email" />
      <Field label="Password" type="password" required value={f.password}
             onChange={(e) => setF({ ...f, password: e.target.value })}
             data-testid="hd-portal-login-password" />
      <BotCheck value={bot} onChange={setBot} testidPrefix="hd-portal-login-botcheck" />
      <button type="submit" disabled={loading || !bot?.ready} data-testid="hd-portal-login-submit"
              className="w-full text-sm font-semibold text-white rounded py-2.5 disabled:opacity-60"
              style={{ background: theme.primary }}>
        {loading ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

function SignupForm({ wsId, theme, onLoggedIn }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [bot, setBot] = useState(null);
  const [loading, setL] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be ≥ 8 characters"); return; }
    setL(true);
    try {
      const r = await axios.post(`${API}/public/client-portal/signup`,
                                  { ...f, workspace_id: wsId, bot_check: bot });
      onLoggedIn(r.data);
      toast.success("Account created");
    } catch (e2) {
      toast.error(apiErrorMessage(e2, "Signup failed"));
    } finally { setL(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="hd-portal-signup-form">
      <Field label="Full name" required value={f.name}
             onChange={(e) => setF({ ...f, name: e.target.value })}
             data-testid="hd-portal-signup-name" />
      <Field label="Email" type="email" required value={f.email}
             onChange={(e) => setF({ ...f, email: e.target.value })}
             data-testid="hd-portal-signup-email" />
      <Field label="Password (min 8 chars)" type="password" required value={f.password}
             onChange={(e) => setF({ ...f, password: e.target.value })}
             data-testid="hd-portal-signup-password" />
      <BotCheck value={bot} onChange={setBot} testidPrefix="hd-portal-signup-botcheck" />
      <button type="submit" disabled={loading || !bot?.ready} data-testid="hd-portal-signup-submit"
              className="w-full text-sm font-semibold text-white rounded py-2.5 disabled:opacity-60"
              style={{ background: theme.primary }}>
        {loading ? "Creating…" : "Create account"}
      </button>
    </form>
  );
}

function MagicLinkForm({ wsId, theme }) {
  const [email, setEmail] = useState("");
  const [bot, setBot] = useState(null);
  const [sent, setSent] = useState(false);
  const [loading, setL] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setL(true);
    try {
      await axios.post(`${API}/public/client-portal/request-magic-link`,
                        { email, workspace_id: wsId, bot_check: bot });
      setSent(true);
    } catch (e2) {
      toast.error(apiErrorMessage(e2, "Failed to send link"));
    } finally { setL(false); }
  };
  if (sent) {
    return (
      <div className="text-center py-4" data-testid="hd-portal-magic-sent">
        <ShieldCheck className="w-8 h-8 mx-auto text-emerald-500" />
        <p className="mt-3 text-sm font-semibold">Check your inbox</p>
        <p className="text-xs text-slate-500 mt-1">
          If we found an account for <span className="font-mono">{email}</span>,
          we've emailed a secure sign-in link. It expires in 48 hours.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="hd-portal-magic-form">
      <p className="text-xs text-slate-500">
        Passwordless sign-in. We'll email you a secure one-time link.
      </p>
      <Field label="Your email" type="email" required value={email}
             onChange={(e) => setEmail(e.target.value)}
             data-testid="hd-portal-magic-email" />
      <BotCheck value={bot} onChange={setBot} testidPrefix="hd-portal-magic-botcheck" />
      <button type="submit" disabled={loading || !bot?.ready} data-testid="hd-portal-magic-submit"
              className="w-full text-sm font-semibold text-white rounded py-2.5 disabled:opacity-60"
              style={{ background: theme.primary }}>
        {loading ? "Sending…" : "Email me a sign-in link"}
      </button>
    </form>
  );
}

// ─────────────────────── Authed shell ───────────────────────

function AuthedShell({ user, theme }) {
  const [view, setView] = useState("list"); // list | new | detail
  const [tickets, setTickets] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/client-portal/my/tickets`,
                                  { headers: authHeaders() });
      setTickets(r.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load tickets"));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadList(); }, []); // eslint-disable-line

  const openDetail = async (tid) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/client-portal/my/tickets/${tid}`,
                                  { headers: authHeaders() });
      setDetail(r.data); setView("detail");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load ticket"));
    } finally { setLoading(false); }
  };

  if (view === "new") {
    return <NewTicket theme={theme} onDone={(t) => {
      loadList();
      if (t?.id) openDetail(t.id);
      else setView("list");
    }} onCancel={() => setView("list")} />;
  }
  if (view === "detail" && detail) {
    return <TicketDetail data={detail} theme={theme}
                          onBack={() => { setDetail(null); loadList(); setView("list"); }}
                          onRefresh={() => openDetail(detail.ticket.id)} />;
  }
  return (
    <div data-testid="hd-portal-list">
      <div className="flex items-end justify-between mt-4 mb-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Hi {(user.name || "").split(" ")[0] || "there"} 👋</h1>
          <p className="text-sm text-slate-500">Your support tickets — {tickets.length} total</p>
        </div>
        <button onClick={() => setView("new")} data-testid="hd-portal-new-ticket-btn"
                className="text-sm font-semibold text-white rounded px-3 py-2 inline-flex items-center gap-1.5"
                style={{ background: theme.primary }}>
          <Plus className="w-4 h-4" /> New ticket
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <select value={statusFilter}
                 onChange={(e) => setStatusFilter(e.target.value)}
                 data-testid="hd-portal-filter-status"
                 className="text-xs h-8 px-2 border border-slate-200 rounded bg-white">
          <option value="all">All tickets</option>
          <option value="open">Open</option>
          <option value="waiting">Waiting</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <div className="flex-1 relative min-w-[160px]">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search subject or ticket #…"
                  data-testid="hd-portal-search"
                  className="w-full h-8 pl-7 pr-2 border border-slate-200 rounded text-xs bg-white focus:outline-none focus:border-slate-400" />
        </div>
      </div>
      {(() => {
        const openSet = new Set(["new", "open", "in_progress", "reopened"]);
        const filtered = tickets.filter((t) => {
          if (statusFilter === "open" && !openSet.has(t.status)) return false;
          if (statusFilter !== "open" && statusFilter !== "all" && t.status !== statusFilter) return false;
          if (search && !(`${t.subject} ${t.ticket_number}`.toLowerCase().includes(search.toLowerCase()))) return false;
          return true;
        });
      return loading ? (
        <div className="text-center text-sm text-slate-400 py-10">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center" data-testid="hd-portal-empty">
          <Ticket className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 font-semibold">
            {tickets.length === 0 ? "No tickets yet" : "No tickets match this filter"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {tickets.length === 0 ? "Click \"New ticket\" to get started." : "Try changing the filter above."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {filtered.map((t) => (
            <button key={t.id} onClick={() => openDetail(t.id)}
                    data-testid={`hd-portal-ticket-row-${t.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 transition-colors">
              <StatusPill status={t.status} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{t.subject}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2">
                  <span className="font-mono">{t.ticket_number}</span>
                  <span>·</span>
                  <span>Opened {new Date(t.created_at).toLocaleDateString()}</span>
                  {t.csat_rating && (
                    <>
                      <span>·</span>
                      <span className="text-amber-600 inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-current" /> {t.csat_rating}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      );
      })()}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-blue-100 text-blue-700",
    open: "bg-emerald-100 text-emerald-700",
    in_progress: "bg-amber-100 text-amber-700",
    waiting: "bg-purple-100 text-purple-700",
    resolved: "bg-slate-200 text-slate-700",
    closed: "bg-slate-100 text-slate-500",
    reopened: "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${map[status] || map.new}`}>
      {(status || "new").replace("_", " ")}
    </span>
  );
}

// ─────────────────────── New ticket ───────────────────────

function NewTicket({ theme, onDone, onCancel }) {
  const [f, setF] = useState({ subject: "", description_html: "" });
  const [loading, setL] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject.trim()) { toast.error("Subject required"); return; }
    setL(true);
    try {
      const r = await axios.post(`${API}/public/client-portal/my/tickets`,
                                  {
                                    subject: f.subject,
                                    description_html: (f.description_html || "").replace(/\n/g, "<br>"),
                                  },
                                  { headers: authHeaders() });
      toast.success(`Ticket ${r.data.ticket_number} created`);
      onDone(r.data);
    } catch (e2) {
      toast.error(apiErrorMessage(e2, "Failed to create ticket"));
    } finally { setL(false); }
  };
  return (
    <div className="max-w-2xl mx-auto mt-4" data-testid="hd-portal-new-ticket-form">
      <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-bold">Submit a new ticket</h2>
        <p className="text-sm text-slate-500">We'll respond within our published SLA. You'll get email updates.</p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <Field label="Subject" required value={f.subject}
                 onChange={(e) => setF({ ...f, subject: e.target.value })}
                 data-testid="hd-portal-new-subject" />
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Details</span>
            <textarea rows={6} value={f.description_html}
                       onChange={(e) => setF({ ...f, description_html: e.target.value })}
                       placeholder="Describe your issue with as much detail as you can share…"
                       data-testid="hd-portal-new-body"
                       className="mt-1 block w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-500" />
          </label>
          <button type="submit" disabled={loading} data-testid="hd-portal-new-submit"
                  className="text-sm font-semibold text-white rounded px-4 py-2.5 disabled:opacity-60"
                  style={{ background: theme.primary }}>
            {loading ? "Creating…" : "Submit ticket"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────── Ticket detail ───────────────────────

function TicketDetail({ data, theme, onBack, onRefresh }) {
  const { ticket, comments } = data;
  const [reply, setReply] = useState("");
  const [posting, setP] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [csatBusy, setCsatBusy] = useState(false);
  const [csatFeedback, setCsatFeedback] = useState("");

  const loadAttachments = async () => {
    try {
      const r = await axios.get(`${API}/public/client-portal/my/tickets/${ticket.id}/attachments`,
                                  { headers: authHeaders() });
      setAttachments(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAttachments(); }, [ticket.id]); // eslint-disable-line

  const send = async () => {
    if (!reply.trim()) return;
    setP(true);
    try {
      await axios.post(`${API}/public/client-portal/my/tickets/${ticket.id}/reply`,
                        { body: reply.replace(/\n/g, "<br>") },
                        { headers: authHeaders() });
      setReply("");
      toast.success("Reply sent");
      onRefresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to send"));
    } finally { setP(false); }
  };
  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/public/client-portal/my/tickets/${ticket.id}/attachments`, fd,
                        { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      loadAttachments();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploading(false); }
  };
  const downloadFile = async (a) => {
    try {
      const r = await axios.get(
        `${API}/public/client-portal/my/tickets/${ticket.id}/attachments/${a.id}/download`,
        { headers: authHeaders(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename;
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const reopen = async () => {
    if (!window.confirm("Reopen this ticket? Our team will be notified.")) return;
    try {
      await axios.post(`${API}/public/client-portal/my/tickets/${ticket.id}/reopen`, {},
                        { headers: authHeaders() });
      toast.success("Ticket reopened");
      onRefresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Reopen failed")); }
  };
  const submitCsat = async (rating) => {
    setCsatBusy(true);
    try {
      await axios.post(`${API}/public/client-portal/my/tickets/${ticket.id}/csat`,
                        { rating, feedback: csatFeedback || null },
                        { headers: authHeaders() });
      toast.success(`Thanks — ${rating}★`);
      onRefresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setCsatBusy(false); }
  };

  const fmtBytes = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : n >= 1024 ? `${(n / 1024).toFixed(0)}KB` : `${n}B`;
  const isResolved = ["resolved", "closed"].includes(ticket.status);

  return (
    <div className="max-w-3xl mx-auto mt-4" data-testid="hd-portal-ticket-detail">
      <button onClick={onBack} data-testid="hd-portal-detail-back"
              className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> All tickets
      </button>
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <StatusPill status={ticket.status} />
          <div className="min-w-0 flex-1">
            <h2 className="font-bold text-base">{ticket.subject}</h2>
            <div className="text-[11px] text-slate-400 mt-0.5">
              <span className="font-mono">{ticket.ticket_number}</span> · Opened {new Date(ticket.created_at).toLocaleString()}
            </div>
          </div>
          {isResolved && (
            <button onClick={reopen} data-testid="hd-portal-reopen"
                     className="text-xs font-semibold border border-slate-300 rounded px-2.5 py-1 hover:bg-slate-50 inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reopen
            </button>
          )}
        </div>
        {ticket.description_html && (
          <div className="mt-4 prose prose-sm max-w-none text-sm text-slate-700"
                dangerouslySetInnerHTML={{ __html: ticket.description_html }} />
        )}
      </div>

      {/* CSAT panel */}
      {isResolved && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4" data-testid="hd-portal-csat">
          {ticket.csat_rating ? (
            <div className="text-sm text-slate-600">
              You rated this ticket <span className="font-semibold text-amber-600">{ticket.csat_rating}★</span>.
              {ticket.csat_feedback && <span className="ml-1 italic">"{ticket.csat_feedback}"</span>}
            </div>
          ) : (
            <div>
              <div className="text-sm font-semibold">How did we do?</div>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => submitCsat(n)} disabled={csatBusy}
                           data-testid={`hd-portal-csat-star-${n}`}
                           className="text-slate-300 hover:text-amber-500 transition-colors">
                    <Star className="w-6 h-6" />
                  </button>
                ))}
              </div>
              <input value={csatFeedback} onChange={(e) => setCsatFeedback(e.target.value)}
                      placeholder="Tell us more (optional)"
                      data-testid="hd-portal-csat-feedback"
                      className="mt-3 w-full h-8 px-2 border border-slate-200 rounded text-xs bg-white focus:outline-none focus:border-slate-400" />
            </div>
          )}
        </div>
      )}

      {/* Attachments */}
      <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4" data-testid="hd-portal-attachments">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2">
          <Paperclip className="w-3.5 h-3.5" /> Attachments {attachments.length > 0 && <span>· {attachments.length}</span>}
        </div>
        {ticket.status !== "closed" && (
          <label className={`flex items-center justify-center gap-1 border-2 border-dashed border-slate-200 py-2 text-xs cursor-pointer hover:bg-slate-50 rounded ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid="hd-portal-attach-upload-label">
            <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Attach a file (max 25 MB)"}
            <input type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0])}
                    data-testid="hd-portal-attach-input" />
          </label>
        )}
        <div className="mt-1.5 space-y-1">
          {attachments.map((a) => {
            const scan = a.scan_status || "unavailable";
            const isMalicious = scan === "malicious";
            const isPending = scan === "pending";
            const isSuspicious = scan === "suspicious";
            const scanColor = isMalicious ? "bg-rose-100 text-rose-800 border-rose-300"
                : isSuspicious ? "bg-amber-100 text-amber-800 border-amber-300"
                : isPending ? "bg-slate-100 text-slate-600 border-slate-300"
                : scan === "clean" ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                : "bg-slate-50 text-slate-500 border-slate-200";
            const scanLabel = isMalicious ? "⚠ Malicious — blocked"
                : isSuspicious ? "⚠ Suspicious"
                : isPending ? "Scanning…"
                : scan === "clean" ? "✓ Clean"
                : scan === "error" ? "Scan error"
                : "";
            return (
              <div key={a.id} className="flex items-center gap-2 text-xs p-1.5 hover:bg-slate-50 rounded"
                    data-testid={`hd-portal-attach-${a.id}`}>
                <Paperclip className="w-3 h-3 text-slate-400" />
                <button onClick={() => !isMalicious && downloadFile(a)}
                         disabled={isMalicious}
                         className={`flex-1 truncate text-left underline decoration-dotted ${isMalicious ? "text-rose-500 line-through cursor-not-allowed" : "text-slate-700"}`}
                         data-testid={`hd-portal-attach-download-${a.id}`}>
                  {a.filename}
                </button>
                {scanLabel && (
                  <span className={`text-[9px] px-1.5 py-0.5 border rounded uppercase tracking-wider ${scanColor}`}
                        title={a.scan_permalink ? `View VT scan · ${a.scan_permalink}` : scanLabel}
                        data-testid={`hd-portal-attach-scan-${a.id}`}>
                    {scanLabel}
                  </span>
                )}
                <span className="text-slate-400">{fmtBytes(a.size)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversation */}
      <div className="mt-4 space-y-3" data-testid="hd-portal-conversation">
        {(comments || []).map((c) => {
          const mine = c.author_kind === "client";
          return (
            <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                mine ? "text-white" : "bg-white border border-slate-200 text-slate-800"
              }`} style={mine ? { background: theme.primary } : {}}>
                <div className="text-[10px] opacity-70 mb-1 uppercase tracking-widest">
                  {mine ? "You" : (c.author_name || "Support")} · {new Date(c.created_at).toLocaleString()}
                </div>
                <div dangerouslySetInnerHTML={{ __html: c.body }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {ticket.status !== "closed" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
          <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                     placeholder="Type your reply…"
                     data-testid="hd-portal-reply-body"
                     className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-500" />
          <div className="flex justify-end mt-2">
            <button onClick={send} disabled={posting || !reply.trim()}
                    data-testid="hd-portal-reply-submit"
                    className="text-sm font-semibold text-white rounded px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
                    style={{ background: theme.primary }}>
              <Send className="w-3.5 h-3.5" />{posting ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
